// One small way to read and write tables. Every screen is a view over the store, so every
// screen reads rows through useRows and writes through the helpers below; a write refreshes
// every screen that shows that table.

import { QueryClient, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from './supabase';

export type Row = Record<string, any>;

/**
 * Each table's stamp (ug.table_stamps: the rows this login can see and their newest change), or null when the project
 * cannot say. The same check the connector makes before it re-reads the library (2026-10-31).
 */
async function tableStamps(tables: readonly string[]): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase.rpc('table_stamps', { p_tables: [...tables] });
  return error || !data || typeof data !== 'object' ? null : (data as Record<string, unknown>);
}

/**
 * Re-reads these tables when the person comes back to this tab after at least a second away: the job tab after a row
 * was signed, or a recipe changed, in the editor's tab (Spencer, 2026-10-16). Queries do not refetch on focus
 * otherwise (above), so a tag that was signed elsewhere would stay until a reload. Since 2026-10-31 (the Supabase usage
 * warning) one small call asks for the tables' stamps first, and only a table whose stamp moved is read again; a table
 * with no stamp, or a project that cannot say, is re-read as before.
 */
export function useRereadOnReturn(tables: readonly string[]) {
  useEffect(() => {
    let awayAt: number | null = null;
    let seen: Record<string, unknown> | null = null;
    void tableStamps(tables).then((s) => (seen = s));
    const away = () => {
      awayAt = awayAt ?? Date.now();
    };
    const back = () => {
      if (document.visibilityState !== 'visible') return;
      const was = awayAt;
      awayAt = null;
      if (was === null || Date.now() - was < 1000) return;
      void tableStamps(tables).then((now) => {
        const before = seen;
        seen = now;
        for (const t of tables) {
          const moved = !before || !now || now[t] === undefined || JSON.stringify(before[t]) !== JSON.stringify(now[t]);
          if (moved) void qc.invalidateQueries({ queryKey: [t] });
        }
      });
    };
    const visibility = () => (document.visibilityState === 'visible' ? back() : away());
    window.addEventListener('blur', away);
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.removeEventListener('blur', away);
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [tables]);
}

export const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 } },
});

export interface Query {
  eq?: Record<string, unknown>;
  in?: Record<string, unknown[]>;
  order?: string[];
  enabled?: boolean;
}

/**
 * The hosted Data API hands back at most a thousand rows a request, whatever limit is asked for, and says nothing
 * about the rest. The time standard table passed a thousand rows on 2026-10-16 (one row per operation per standards
 * version), and the newest rows, the minutes Spencer had just typed on three custom labor operations, never reached
 * the app: "it still says that no time standard has been set yet". So a read goes a page at a time until a short
 * page comes back, every page in the same order (the order asked for, then by id, so a row is never on two pages).
 */
const PAGE = 1000;
/** The relations without an id column (two views and the permission areas): small, and paged without the tiebreak. */
const NO_ID = new Set(['material_price_history', 'material_usage_monthly', 'permission_area']);

async function fetchPage(table: string, q: Query, from: number, tiebreak: boolean): Promise<Row[]> {
  let req = supabase.from(table).select('*');
  for (const [k, v] of Object.entries(q.eq ?? {})) req = v === null ? req.is(k, null) : req.eq(k, v as any);
  for (const [k, v] of Object.entries(q.in ?? {})) req = req.in(k, v as any[]);
  const order = q.order ?? [];
  for (const o of order) {
    const desc = o.startsWith('-');
    req = req.order(desc ? o.slice(1) : o, { ascending: !desc });
  }
  if (tiebreak && !order.some((o) => o === 'id' || o === '-id')) req = req.order('id', { ascending: true });
  const { data, error } = await req.range(from, from + PAGE - 1);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchRows(table: string, q: Query = {}): Promise<Row[]> {
  // one request, in the order asked for (or the table's own), as before, is the whole of a table under a thousand rows
  const first = await fetchPage(table, q, 0, false);
  if (first.length < PAGE) return first;
  // a bigger table is read again from the top with the id tiebreak, so a row is never on two pages or on none
  const out: Row[] = [];
  for (let from = 0; ; from += PAGE) {
    const page = await fetchPage(table, q, from, !NO_ID.has(table));
    out.push(...page);
    if (page.length < PAGE) return out;
  }
}

export function useRows(table: string, q: Query = {}): { rows: Row[]; loading: boolean; error: Error | null } {
  const emptyIn = Object.values(q.in ?? {}).some((v) => v.length === 0);
  const res = useQuery({
    queryKey: [table, q.eq ?? null, q.in ?? null, q.order ?? null],
    queryFn: () => fetchRows(table, q),
    enabled: (q.enabled ?? true) && !emptyIn,
  });
  return { rows: emptyIn ? EMPTY : res.data ?? EMPTY, loading: res.isLoading && !emptyIn, error: (res.error as Error) ?? null };
}
const EMPTY: Row[] = [];

// ---------- writes ----------

type Listener = (message: string) => void;
let listener: Listener | null = null;
export function onToast(fn: Listener) {
  listener = fn;
}
export function toast(message: string) {
  listener?.(message);
}

/** Whether this login may write at all. The session sets it from the role; a read-only login's write stops here with one message. */
let writable = true;
export const READ_ONLY = 'Read only: only the owner can change this';
export function setWritable(v: boolean) {
  writable = v;
}
function assertWritable() {
  if (writable) return;
  toast(READ_ONLY);
  throw new Error(READ_ONLY);
}

async function done<T>(table: string | string[], p: PromiseLike<{ data: T; error: { message: string } | null }>): Promise<T> {
  assertWritable();
  const { data, error } = await p;
  if (error) {
    toast(`Could not save: ${error.message}`);
    throw new Error(error.message);
  }
  for (const t of Array.isArray(table) ? table : [table]) await qc.invalidateQueries({ queryKey: [t] });
  return data;
}

export async function insertRow(table: string, row: Row): Promise<Row> {
  return (await done(table, supabase.from(table).insert(row).select().single())) as Row;
}
export async function insertRows(table: string, rows: Row[]): Promise<Row[]> {
  if (rows.length === 0) return [];
  return (await done(table, supabase.from(table).insert(rows).select())) ?? [];
}
export async function updateRow(table: string, id: string, patch: Row): Promise<Row> {
  return (await done(table, supabase.from(table).update(patch).eq('id', id).select().single())) as Row;
}
/**
 * The same change to several rows of one table, in one step (verifying everything ticked). `only` guards it: a row
 * is changed only while its columns still hold those values (null = still blank). Returns the ids it changed.
 */
export async function updateRows(table: string, ids: string[], patch: Row, only: Row = {}): Promise<string[]> {
  if (ids.length === 0) return [];
  let req = supabase.from(table).update(patch).in('id', ids);
  for (const [k, v] of Object.entries(only)) req = v === null ? req.is(k, null) : req.eq(k, v);
  const rows = (await done(table, req.select('id'))) as Row[] | null;
  return (rows ?? []).map((r) => r.id as string);
}
export async function deleteRow(table: string, id: string): Promise<void> {
  await done(table, supabase.from(table).delete().eq('id', id));
}
export async function upsertRow(table: string, row: Row, onConflict: string): Promise<Row> {
  return (await done(table, supabase.from(table).upsert(row, { onConflict }).select().single())) as Row;
}
export async function rpc<T = unknown>(fn: string, args: Row = {}): Promise<T> {
  assertWritable();
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    toast(`Could not save: ${error.message}`);
    throw new Error(error.message);
  }
  return data as T;
}
export async function findRow(table: string, match: Row): Promise<Row | null> {
  const { data, error } = await supabase.from(table).select('*').match(match).limit(1);
  if (error) throw new Error(error.message);
  return data?.[0] ?? null;
}
export function refresh(...tables: string[]) {
  for (const t of tables) void qc.invalidateQueries({ queryKey: [t] });
}
